import React, { useMemo, ReactNode } from "react";
import {
  _Chef2024Container,
  _Chef2024Left,
  _Chef2024Right,
  _Chef2024RightWrapper,
  _LandingInnovationProductList,
  _LandingInnovationRoot,
} from "../LandingInnovation.styled";
import htmlParse from "html-react-parser";
import clsx from "clsx";
import FlexBox from "../../../FlexBox/FlexBox";
import InnovationProduct, {
  IInnovationProductProps,
} from "../InnovationProduct/InnovationProduct";
import Typography from "../../../../Foundation/Typography/Typography";
import { _InnovationMargauxMotinRoot } from "./InnovationMargauxMotin.styled";

export type IInnovationMargauxMotinProduct<T extends any = any> = {
  id_product: string;
  imageId: string;
  title: string;
  subtitle?: string;
  description?: ReactNode;
  price?: string;
  priceSubtitle?: string;
  ctaLabel?: string;
  color?: string;
  displayFromMention?: string;
  displayPrice?: boolean;
} & T;

interface IInnovationMargauxMotinProps<T extends any = any> {
  title: string;
  highlightedTitle?: string;
  description?: string;
  coverImageId?: string;
  products?: IInnovationMargauxMotinProduct<T>[];
  onClickProduct?: (product: IInnovationMargauxMotinProduct<T>) => void;
  showLessLabel: string;
  showMoreLabel: string;
  renderProduct?: (data: IInnovationProductProps, index: number) => ReactNode;
}
const InnovationMargauxMotin = <T extends any = any>(
  props: IInnovationMargauxMotinProps<T>
) => {
  const {
    title,
    description,
    products,
    onClickProduct,
    showLessLabel,
    showMoreLabel,
    renderProduct,
  } = props;

  const productList = useMemo(() => {
    return products?.slice(0, 3) || [];
  }, [products]);

  const itemRenderer = (
    product: IInnovationMargauxMotinProduct<T>,
    index: number,
    selectiveView?: IInnovationProductProps["selectiveView"]
  ) => {
    const _data: IInnovationProductProps = {
      className: "innovation-product-item",
      product: product,
      variant: "text-on-desktop",
      onClick: onClickProduct,
      showLessLabel: showLessLabel,
      showMoreLabel: showMoreLabel,
      selectiveView,
    };
    const showBreak = index > 1 && index % 3 === 0;
    return (
      <React.Fragment key={product.id_product}>
        {showBreak && (
          <div
            className="break"
            style={{
              flexBasis: "100%",
              height: 0,
            }}
          ></div>
        )}
        {renderProduct ? (
          renderProduct(_data, index)
        ) : (
          <InnovationProduct key={product.id_product} {..._data} />
        )}
      </React.Fragment>
    );
  };

  return (
    <_InnovationMargauxMotinRoot
      as={_LandingInnovationRoot}
      className="landing-innovations"
    >
      <div className={clsx("landing-innovations__container")}>
        {/* header */}
        <FlexBox
          direction="column"
          align="center"
          className="landing-innovations__header"
        >
          <Typography
            variant={{ lg: "h3", mobile: "h6" }}
            align="center"
            className="landing-innovations__title"
          >
            {title}
          </Typography>
          {description && (
            <Typography
              variant="body"
              align="center"
              className="landing-innovations__description"
            >
              {htmlParse(description + "")}
            </Typography>
          )}
        </FlexBox>

        {/* Product list */}
        {
          <_LandingInnovationProductList
            $isList={true}
            $itemFullWidth={productList.length < 3}
          >
            <FlexBox
              justify="center"
              style={{
                maxWidth: 1118,
                width: "100%",
                scrollSnapAlign: "start",
                margin: "0 auto",
              }}
            >
              <FlexBox
                className={"innovation-product-list-wrapper"}
                justify={"space-between"}
              >
                {productList?.map((product, index) => {
                  return itemRenderer(product, index);
                })}
              </FlexBox>
            </FlexBox>
          </_LandingInnovationProductList>
        }
      </div>
    </_InnovationMargauxMotinRoot>
  );
};

export default InnovationMargauxMotin;
